/**
 * Accessibility Statement Page
 * Details CalWMP's commitment to digital accessibility and conformance status
 */

import { Link } from 'react-router-dom';

export function Accessibility() {
  const lastUpdated = '2025-09-30';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
          ← Back to Home
        </Link>
      </div>

      <h1 className="text-4xl font-bold text-neutral-900 mb-4">Accessibility Statement</h1>

      <p className="text-sm text-neutral-600 mb-8">
        <strong>Last Updated:</strong> {lastUpdated}
      </p>

      <div className="prose prose-neutral max-w-none">
        {/* Commitment to Accessibility */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Our Commitment to Accessibility</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP is committed to ensuring digital accessibility for people with disabilities. We are continually
            improving the user experience for everyone and applying the relevant accessibility standards to ensure
            we provide equal access to all users.
          </p>
          <p className="text-neutral-700 leading-relaxed">
            We believe that everyone should be able to create compliant waste management plans, regardless of their
            abilities. Accessibility is not just a legal requirement&mdash;it is a core value that guides our design and
            development decisions.
          </p>
        </section>

        {/* Conformance Status */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Conformance Status</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            The Web Content Accessibility Guidelines (WCAG) defines requirements for designers and developers to
            improve accessibility for people with disabilities. It defines three levels of conformance: Level A,
            Level AA, and Level AAA.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-4">
            <p className="text-blue-900 mb-2">
              <strong>Current Status:</strong> CalWMP is partially conformant with WCAG 2.1 level AA.
            </p>
            <p className="text-blue-900">
              Partially conformant means that some parts of the content do not fully conform to the accessibility
              standard. We are actively working to achieve full conformance.
            </p>
          </div>
        </section>

        {/* Technical Specifications */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Technical Specifications</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Accessibility of CalWMP relies on the following technologies to work with the particular combination
            of web browser and any assistive technologies or plugins installed on your computer:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>HTML5:</strong> Semantic markup for proper structure and meaning</li>
            <li><strong>WAI-ARIA:</strong> Accessible Rich Internet Applications attributes for dynamic content</li>
            <li><strong>CSS3:</strong> Visual presentation separate from content</li>
            <li><strong>JavaScript:</strong> Enhanced interactivity with graceful degradation</li>
            <li><strong>TypeScript:</strong> Type-safe code for more reliable implementations</li>
            <li><strong>React:</strong> Component-based architecture with accessibility in mind</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed">
            These technologies are relied upon for conformance with the accessibility standards used.
          </p>
        </section>

        {/* Current Accessibility Features */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Current Accessibility Features</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP includes the following accessibility features:
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Keyboard Navigation</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>All interactive elements are keyboard accessible</li>
            <li>Logical tab order throughout the application</li>
            <li>Visible focus indicators on all focusable elements</li>
            <li>Keyboard shortcuts for common actions (where applicable)</li>
            <li>Skip navigation links to bypass repetitive content</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Screen Reader Support</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Semantic HTML elements for proper document structure</li>
            <li>ARIA labels and descriptions for complex components</li>
            <li>ARIA live regions for dynamic content updates</li>
            <li>Alternative text for all meaningful images</li>
            <li>Form labels properly associated with inputs</li>
            <li>Error messages announced and clearly identified</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Visual Design</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Color contrast ratios meeting WCAG AA standards (4.5:1 for normal text, 3:1 for large text)</li>
            <li>Information not conveyed by color alone</li>
            <li>Resizable text up to 200% without loss of functionality</li>
            <li>Clear and consistent visual hierarchy</li>
            <li>Sufficient spacing between interactive elements</li>
            <li>Responsive design for various screen sizes and zoom levels</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Content Structure</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Clear and descriptive page titles</li>
            <li>Properly nested heading structure (h1 through h6)</li>
            <li>Descriptive link text (avoiding &quot;click here&quot;)</li>
            <li>Lists marked up with appropriate HTML elements</li>
            <li>Tables with proper headers and captions where used</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Forms and Input</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Clear and descriptive form labels</li>
            <li>Error identification and suggestions for correction</li>
            <li>Input validation with accessible error messages</li>
            <li>Required fields clearly marked</li>
            <li>Input purpose identified (where applicable with autocomplete attributes)</li>
          </ul>
        </section>

        {/* Known Limitations */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Known Limitations</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Despite our best efforts to ensure accessibility, there may be some limitations. Below are known issues
            we are working to address:
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 my-4">
            <h3 className="text-lg font-semibold text-amber-900 mb-3">Areas Under Improvement:</h3>
            <ul className="list-disc list-inside space-y-2 text-amber-900">
              <li>
                <strong>PDF Generation:</strong> Generated PDF documents may not be fully accessible to screen readers.
                We are exploring alternative export formats and improved PDF accessibility.
              </li>
              <li>
                <strong>Complex Data Tables:</strong> Some tables with multiple data dimensions may be challenging to
                navigate with assistive technologies. We are working on improved table navigation.
              </li>
              <li>
                <strong>Third-Party Content:</strong> Some embedded content or external links may not meet our
                accessibility standards. We are working with vendors to improve accessibility.
              </li>
              <li>
                <strong>Mobile Experience:</strong> While responsive, some touch interactions may need refinement
                for users with motor disabilities. Ongoing improvements are in progress.
              </li>
            </ul>
          </div>

          <p className="text-neutral-700 leading-relaxed">
            We are actively working to remediate these issues and achieve full conformance. If you encounter any
            accessibility barriers not listed here, please let us know.
          </p>
        </section>

        {/* Assessment Approach */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Assessment Approach</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP&apos;s accessibility has been assessed using the following methods:
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Self-Evaluation</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Regular internal audits using automated accessibility testing tools</li>
            <li>Manual testing with keyboard-only navigation</li>
            <li>Screen reader testing with NVDA, JAWS, and VoiceOver</li>
            <li>Color contrast analysis tools</li>
            <li>Code reviews with accessibility checklists</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Testing Tools Used</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>axe DevTools for automated accessibility scanning</li>
            <li>WAVE Web Accessibility Evaluation Tool</li>
            <li>Lighthouse accessibility audits</li>
            <li>Color Contrast Analyzers</li>
            <li>Keyboard navigation testing</li>
          </ul>

          <p className="text-neutral-700 leading-relaxed">
            This statement was created on {lastUpdated} using a combination of automated testing and manual evaluation.
          </p>
        </section>

        {/* Feedback Process */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Feedback and Contact Information</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We welcome your feedback on the accessibility of CalWMP. Please let us know if you encounter accessibility
            barriers:
          </p>

          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6 my-4">
            <h3 className="text-lg font-semibold text-neutral-900 mb-3">How to Report Accessibility Issues:</h3>
            <p className="text-neutral-700 mb-4">
              <strong>Email:</strong> <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 underline">support@calwmp.com</a>
            </p>
            <p className="text-neutral-700 mb-4">
              <strong>Subject Line:</strong> Please include &quot;Accessibility Issue&quot; in your subject line
            </p>
            <p className="text-neutral-700 mb-4">
              <strong>Information to Include:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
              <li>Description of the accessibility barrier you encountered</li>
              <li>The page or feature where the issue occurred (URL if possible)</li>
              <li>Your web browser and version</li>
              <li>Assistive technology you were using (if applicable)</li>
              <li>Any other relevant details that might help us understand the issue</li>
            </ul>
            <p className="text-neutral-700">
              <strong>Response Time:</strong> We aim to respond to all accessibility feedback within 5 business days
              and will work with you to resolve issues as quickly as possible.
            </p>
          </div>

          <p className="text-neutral-700 leading-relaxed">
            Your feedback helps us improve accessibility for all users. We take all accessibility reports seriously
            and prioritize fixes based on impact and severity.
          </p>
        </section>

        {/* Compatibility Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Compatibility with Browsers and Assistive Technology</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP is designed to be compatible with the following assistive technologies and browser combinations:
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Supported Screen Readers</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>NVDA:</strong> Latest version with Chrome, Firefox, or Edge on Windows</li>
            <li><strong>JAWS:</strong> Version 2021 or later with Chrome, Firefox, or Edge on Windows</li>
            <li><strong>VoiceOver:</strong> Latest version with Safari on macOS and iOS</li>
            <li><strong>TalkBack:</strong> Latest version with Chrome on Android</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Supported Browsers</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Google Chrome (latest version)</li>
            <li>Mozilla Firefox (latest version)</li>
            <li>Microsoft Edge (latest version)</li>
            <li>Safari (latest version on macOS and iOS)</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Other Assistive Technologies</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Keyboard-only navigation</li>
            <li>Voice control software (Dragon NaturallySpeaking, Voice Control)</li>
            <li>Screen magnification software</li>
            <li>Browser zoom functionality (up to 200%)</li>
            <li>Custom color schemes and high contrast modes</li>
          </ul>

          <p className="text-neutral-700 leading-relaxed">
            CalWMP may not be fully compatible with older browsers or assistive technology versions. For the best
            experience, we recommend using the latest versions of supported browsers and assistive technologies.
          </p>
        </section>

        {/* Limitations and Alternatives */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Limitations and Alternatives</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Despite our best efforts to ensure accessibility of CalWMP, there may be some limitations. If you
            experience difficulty using any part of our service, we are committed to working with you to provide
            alternatives or assistance.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Available Alternatives</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>
              <strong>Customer Support:</strong> Our support team can assist you in completing tasks or accessing
              information. Contact us at <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 underline">support@calwmp.com</a>.
            </li>
            <li>
              <strong>Alternative Formats:</strong> If you need information in a different format (large print,
              accessible electronic format, etc.), please contact us and we will work to accommodate your needs.
            </li>
            <li>
              <strong>Assisted Plan Creation:</strong> If you encounter barriers to creating a plan independently,
              our team can assist you through the process via email or scheduled consultation.
            </li>
          </ul>
        </section>

        {/* Formal Complaints */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Formal Complaints</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            If you are not satisfied with our response to your accessibility feedback, you may file a formal complaint:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>
              <strong>With Us:</strong> Contact us at <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 underline">support@calwmp.com</a> with
              &quot;Formal Accessibility Complaint&quot; in the subject line. Include details of your concern and previous
              communications. We will escalate the issue to management for review.
            </li>
            <li>
              <strong>External Organizations:</strong> You may also file a complaint with relevant regulatory bodies
              or advocacy organizations if you believe CalWMP is not meeting legal accessibility requirements.
            </li>
          </ul>
        </section>

        {/* Ongoing Efforts */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Ongoing Accessibility Efforts</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We are committed to continual accessibility improvement. Our ongoing efforts include:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Regular accessibility audits and testing</li>
            <li>Staff training on accessibility best practices</li>
            <li>Incorporating accessibility into our development workflow</li>
            <li>Engaging with the disability community for feedback</li>
            <li>Staying current with WCAG guidelines and best practices</li>
            <li>Monitoring emerging accessibility standards and technologies</li>
            <li>Setting measurable accessibility goals and tracking progress</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed">
            Accessibility is an ongoing journey, not a destination. We are committed to making CalWMP accessible to
            all users and appreciate your patience and feedback as we work toward full conformance.
          </p>
        </section>

        {/* Contact Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Contact Us</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            If you have questions or need assistance regarding accessibility, please contact us:
          </p>
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6">
            <p className="text-neutral-900 mb-2">
              <strong>Email:</strong> <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 underline">support@calwmp.com</a>
            </p>
            <p className="text-neutral-900 mb-2">
              <strong>Subject:</strong> Include &quot;Accessibility&quot; in your subject line for priority handling
            </p>
            <p className="text-neutral-900">
              <strong>Response Time:</strong> We aim to respond to all accessibility inquiries within 5 business days.
            </p>
          </div>
        </section>

        {/* Related Policies */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Related Policies</h2>
          <ul className="space-y-2 text-neutral-700">
            <li>
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link to="/terms" className="text-primary-600 hover:text-primary-700 underline">
                Terms of Service
              </Link>
            </li>
            <li>
              <Link to="/disclaimer" className="text-primary-600 hover:text-primary-700 underline">
                Disclaimer
              </Link>
            </li>
          </ul>
        </section>

        {/* Additional Resources */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Additional Resources</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            For more information about web accessibility and assistive technologies:
          </p>
          <ul className="space-y-2 text-neutral-700">
            <li>
              <a href="https://www.w3.org/WAI/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">
                Web Accessibility Initiative (WAI)
              </a>
            </li>
            <li>
              <a href="https://www.w3.org/WAI/WCAG21/quickref/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">
                WCAG 2.1 Quick Reference
              </a>
            </li>
            <li>
              <a href="https://www.ada.gov/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">
                Americans with Disabilities Act (ADA)
              </a>
            </li>
            <li>
              <a href="https://webaim.org/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">
                WebAIM (Web Accessibility In Mind)
              </a>
            </li>
          </ul>
        </section>
      </div>

      <div className="mt-12 pt-6 border-t border-neutral-200">
        <p className="text-sm text-neutral-600">
          This Accessibility Statement was last updated on {lastUpdated}. We review and update this statement
          regularly to reflect our ongoing accessibility improvements.
        </p>
      </div>
    </div>
  );
}
